import React from 'react';
import { Calculator, Calendar, Wallet, Info, Users } from 'lucide-react';
import { Country } from '../../../types/todo';
import { CountrySelector } from './CountrySelector';
import { CityAndHousing } from './CityAndHousing';
import { BudgetNotice } from '../BudgetNotice';
import { FLIGHT_RANGES } from '../../../constants/visa-costs';
import { Switch } from '../../../components/ui/Switch';

interface StepOneProps {
  formData: {
    country: string;
    city: string;
    housingType: string;
    initialSavings: string;
    pvtDuration: string;
    isCouple: boolean;
  };
  countries: Country[];
  onFormDataChange: (data: any) => void;
}

export function StepOne({ formData, countries, onFormDataChange }: StepOneProps) {
  // Mettre à jour les valeurs par défaut quand le pays change
  React.useEffect(() => {
    if (formData.country) {
      const flightRange = FLIGHT_RANGES[formData.country];
      onFormDataChange({
        ...formData,
        pvtDuration: formData.country === 'ca' ? '24' : '12',
        initialSavings: formData.initialSavings || '5000',
        flightCost: flightRange ? flightRange.avg.toString() : '700'
      });
    }
  }, [formData.country]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-premium p-6 sm:p-8 border border-gray-200/50 dark:border-gray-700/50">
      <BudgetNotice
        message="Les montants affichés sont des estimations basées sur des moyennes issues de l'API Numbeo, reconnue pour sa fiabilité. Les coûts réels peuvent varier en fonction de votre situation, de la période et de la zone géographique."
      />

      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl">
          <Calculator className="w-6 h-6 text-primary-500 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Configuration initiale
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Définissons les bases de votre aventure
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-800/40 rounded-xl p-5 border border-gray-200/50 dark:border-gray-700/50 hover:border-primary-200 dark:hover:border-primary-800/50 transition-colors duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                <Users className="w-5 h-5 text-primary-500 dark:text-primary-400" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Mode couple
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Calculez votre budget pour deux personnes
                </p>
              </div>
            </div>
            <Switch
              checked={formData.isCouple}
              onCheckedChange={(checked) => onFormDataChange({ ...formData, isCouple: checked })}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-800/40 rounded-xl p-5 border border-gray-200/50 dark:border-gray-700/50 hover:border-primary-200 dark:hover:border-primary-800/50 transition-colors duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <Wallet className="w-5 h-5 text-primary-500 dark:text-primary-400" />
            </div>
            <div>
              <label className="block text-base font-semibold text-gray-900 dark:text-white">
                Économies <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Budget disponible pour votre PVT
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <span className="text-gray-400 dark:text-gray-500 font-medium text-sm">EUR</span>
            </div>
            <input
              type="number"
              value={formData.initialSavings}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value));
                onFormDataChange({ ...formData, initialSavings: value.toString() });
              }}
              min="0"
              placeholder="2500"
              className="w-full pl-14 pr-4 py-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white transition-all text-lg font-medium"
              required
            />
          </div>
        </div>

        <CountrySelector
          formData={formData}
          countries={countries}
          onFormDataChange={onFormDataChange}
          required
        />

        {formData.country && (
          <CityAndHousing
            formData={formData}
            onFormDataChange={onFormDataChange}
            onError={() => {}}
            countries={countries}
            required
          />
        )}

        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-800/40 rounded-xl p-5 border border-gray-200/50 dark:border-gray-700/50 hover:border-primary-200 dark:hover:border-primary-800/50 transition-colors duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <Calendar className="w-5 h-5 text-primary-500 dark:text-primary-400" />
            </div>
            <div>
              <label className="block text-base font-semibold text-gray-900 dark:text-white">
                Durée du PVT <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                En mois
              </p>
            </div>
          </div>
          <input
            type="number"
            value={formData.pvtDuration}
            onChange={(e) => onFormDataChange({ ...formData, pvtDuration: e.target.value })}
            min="1"
            max="24"
            className="w-full px-4 py-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white transition-all text-lg font-medium"
          />
          {formData.country === 'ca' && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-accent-50 dark:bg-accent-900/10 rounded-lg border border-accent-100/50 dark:border-accent-800/30">
              <Info className="w-4 h-4 text-accent-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-accent-700 dark:text-accent-400">Le PVT Canada peut durer jusqu'à 24 mois pour les Français et jusqu'à 12 mois pour les Belges.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}